import { getToken, setGuestIntakeToken } from "@/services/authStorage";
import { apiClient } from "@/services/apiClient";
import { readInternalSource } from "@/lib/mag-analytics";

/**
 * Mirrors MAX_INTAKE_TURNS on the backend. Used ONLY to seed the counter
 * before the first turn returns a real value — the server is authoritative
 * and enforces the cap regardless of what the client believes.
 */
export const QUOTE_INTAKE_MAX_TURNS = 12;

/** Mirrors QuoteIntakeConversationEntity['status'] on the backend. */
export type QuoteIntakeStatus =
  | "active"
  | "drafted"
  | "submitted"
  /** Already sold at a fixed price — no RFQ created, consumer sent to the product. */
  | "out_of_scope"
  | "abandoned";

export interface QuoteIntakeMessage {
  role: "consumer" | "assistant";
  content: string;
  at: string;
}

import type { QuoteIntakeAnticipatedDocument } from "@/services/quote-intake-items";

// Re-exported so every existing import site keeps working: these three moved
// to a module free of `apiClient`, so the pure slot algebra (and its spec) can
// load them without pulling `import.meta.env`.
export type { QuoteIntakeItemKind, QuoteIntakeAnticipatedDocument } from "@/services/quote-intake-items";
export { anticipatedItemKind } from "@/services/quote-intake-items";

export interface QuoteIntakeDraft {
  title: string;
  summary: string;
  /** Null when the conversation ran out of turns without establishing one. */
  discipline: "notary" | "accountant" | null;
  /**
   * A second discipline, when the job has two separable pieces of work — e.g.
   * a SAS, where the contador does the alta impositiva and the escribano the
   * BCU declaration. Each is quoted and paid separately.
   */
  alsoNeeds?: "notary" | "accountant" | null;
  confidence: number;
  rationale: string;
  /** Absent on conversations drafted before spec c04ed435 — always guard. */
  anticipatedDocuments?: QuoteIntakeAnticipatedDocument[];
  /**
   * Does the trámite end in paper that has to reach the consumer (spec
   * 8c2aab49 D2)? When true the wizard asks for a delivery address and every
   * quote carries a shipping charge.
   *
   * Absent on conversations drafted before this shipped — guard, and read
   * absence as false: a request that never declared physical delivery must not
   * start demanding an address retroactively.
   */
  producesPhysicalDocument?: boolean;
}

export interface QuoteIntakeConversation {
  conversationId: string;
  status: QuoteIntakeStatus;
  messages: QuoteIntakeMessage[];
  draft: QuoteIntakeDraft | null;
  turnsRemaining: number;
}

export interface QuoteIntakeTurnResponse {
  conversationId: string;
  status: QuoteIntakeStatus;
  assistantMessage: string;
  draft: QuoteIntakeDraft | null;
  finished: boolean;
  turnsRemaining: number;
}

/**
 * Start a conversation, with or without an account (spec b5b59843).
 *
 * TWO ROUTES, ONE RETURN SHAPE. An authenticated visitor posts to
 * `me/quote-intake` as before. An anonymous one posts to `public/quote-intake`,
 * which mints a guest identity and hands back a token alongside the
 * conversation; that token is stored in its own slot and sent on intake calls
 * only.
 *
 * The caller does not branch on this. Which route runs is decided here,
 * because "is there a session" is the only difference and every caller would
 * otherwise have to ask the same question and get it right.
 */
export async function startQuoteIntake(
  options?: { captchaToken?: string },
): Promise<QuoteIntakeConversation> {
  // Attributed at START, not only at submit. A conversation that is abandoned
  // or that the catalog guard redirects never becomes a quote_request, so
  // quote_request.entrySource cannot see either — and those are exactly the
  // two numbers the entry-page funnel is measured on.
  const entrySource = readInternalSource();
  const body = {
    ...(entrySource ? { entrySource } : {}),
  };

  if (getToken()) {
    const res = await apiClient.post<QuoteIntakeConversation>("/me/quote-intake", body);
    return res.data;
  }

  const res = await apiClient.post<QuoteIntakeConversation & { accessToken: string }>(
    "/public/quote-intake",
    { ...body, ...(options?.captchaToken ? { captchaToken: options.captchaToken } : {}) },
  );
  /*
    STORED BEFORE THE CONVERSATION IS RETURNED, so there is no window in which
    the caller holds a conversation id it cannot authenticate against. The id
    goes into the URL immediately after this resolves; a token written later
    would leave a reload in between unable to reopen it.
  */
  setGuestIntakeToken(res.data.accessToken);
  return res.data;
}

/**
 * Read back a conversation the consumer already started.
 *
 * EXISTS SO A REFRESH DOES NOT DESTROY AN INTAKE. The conversation id rides in
 * the URL from the first message onward, so reloading rehydrates instead of
 * landing the consumer back on "¿Qué necesitás?" having silently lost the
 * turns they already answered.
 *
 * OWNERSHIP IS THE SERVER'S CALL, never the id's. The backend loads this with
 * `where: { id, startedByUserId }` and 404s otherwise, so the uuid is an
 * identifier and not a capability — a link to someone else's conversation
 * resolves to nothing. Do not add a client-side guard that implies otherwise.
 *
 * `quoteRequestId` is present only once the conversation produced a request;
 * it is absent on every conversation still being drafted.
 */
export async function getQuoteIntake(
  conversationId: string,
): Promise<QuoteIntakeConversation & { quoteRequestId: string | null }> {
  const res = await apiClient.get<QuoteIntakeConversation & { quoteRequestId: string | null }>(
    `/me/quote-intake/${conversationId}`,
  );
  return res.data;
}

export async function sendQuoteIntakeMessage(
  conversationId: string,
  content: string,
): Promise<QuoteIntakeTurnResponse> {
  const res = await apiClient.post<QuoteIntakeTurnResponse>(
    `/me/quote-intake/${conversationId}/messages`,
    { content },
  );
  return res.data;
}

/**
 * A 503 from any intake route means the model is unavailable, and the caller
 * MUST drop the consumer onto the static title/description form rather than
 * leaving them in a chat that cannot answer. Intake is never blocked by the AI
 * being down — spec add326e0 §4, the design's most important failure mode.
 */
export function isIntakeUnavailable(error: unknown): boolean {
  const status = (error as { response?: { status?: unknown } } | null)?.response?.status;
  return status === 503;
}

/**
 * Move a guest conversation onto the account that just signed in.
 *
 * Carries the GUEST token in the body as proof of the source, while the
 * request itself is authenticated as the destination — a conversation id alone
 * would let anyone claim anyone's chat by guessing a uuid.
 *
 * Only needed on the sign-in path. Registering promotes the guest row in place
 * server-side, so the conversation never changes hands.
 */
export async function claimGuestIntake(guestAccessToken: string): Promise<number> {
  const res = await apiClient.post<{ claimed: number }>("/me/quote-intake/claim", {
    guestAccessToken,
  });
  return res.data.claimed;
}
